import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTranslation } from '../../../../i18n';
import { LongestWordLetterRow } from './LongestWordLetterRow';
import './longest-word.css';

export interface LongestWordPlayerHandle {
  getAnswer: () => string;
}

export interface LongestWordPlayerProps {
  letters: string;
  disabled?: boolean;
  /** Intro window: show rolling tiles instead of the interactive board. */
  isIntro?: boolean;
  onAnswerChange?: (answer: string) => void;
}

const LETTER_MAX_PX = 48;
const GAP_PX = 5;
const ROW_GAP_PX = 20;

function letterMetricsForCount(n: number): { letterMaxPx: number; gapPx: number } {
  if (n >= 10) return { letterMaxPx: 36, gapPx: 3 };
  if (n >= 9) return { letterMaxPx: 40, gapPx: 4 };
  if (n >= 8) return { letterMaxPx: 44, gapPx: 4 };
  return { letterMaxPx: LETTER_MAX_PX, gapPx: GAP_PX };
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Word Order–style pool + variable-length slots. Editable until disabled (time-up).
 * Contiguous left-to-right placed letters form the flushed answer.
 */
export const LongestWordPlayer = forwardRef<LongestWordPlayerHandle, LongestWordPlayerProps>(
  function LongestWordPlayer({ letters, disabled = false, isIntro = false, onAnswerChange }, ref) {
    const { t } = useTranslation();
    const normalized = useMemo(
      () => (letters ?? '').toUpperCase().replace(/[^A-Z]/g, '').slice(0, 10),
      [letters]
    );
    const n = Math.max(normalized.length, 10);
    const initialPool = useMemo(
      () => shuffle((normalized.length === 10 ? normalized : normalized.padEnd(10, 'A')).split('')),
      [normalized]
    );

    const [slots, setSlots] = useState<(string | null)[]>(() => Array(n).fill(null));
    const [pool, setPool] = useState<string[]>(() => [...initialPool]);
    const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
    const [dragging, setDragging] = useState<{ source: 'pool' | 'slot'; index: number } | null>(null);

    const slotsRef = useRef(slots);
    const poolRef = useRef(pool);
    slotsRef.current = slots;
    poolRef.current = pool;

    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    // Reset board when letter set changes
    useEffect(() => {
      const base = normalized.length === 10 ? normalized : normalized.padEnd(10, 'A');
      const nextPool = shuffle(base.split(''));
      const nextSlots = Array(10).fill(null) as (string | null)[];
      slotsRef.current = nextSlots;
      poolRef.current = nextPool;
      setSlots(nextSlots);
      setPool(nextPool);
      setSelectedSlot(null);
    }, [normalized]);

    const commitBoard = (nextSlots: (string | null)[], nextPool: string[]) => {
      slotsRef.current = nextSlots;
      poolRef.current = nextPool;
      setSlots(nextSlots);
      setPool(nextPool);
    };

    const currentWord = useMemo(() => {
      // Contiguous from the left until first empty
      const parts: string[] = [];
      for (const s of slots) {
        if (s == null) break;
        parts.push(s);
      }
      return parts.join('');
    }, [slots]);

    useEffect(() => {
      onAnswerChange?.(currentWord);
    }, [currentWord, onAnswerChange]);

    useImperativeHandle(
      ref,
      () => ({
        getAnswer: () => {
          const cur = slotsRef.current;
          const parts: string[] = [];
          for (const s of cur) {
            if (s == null) break;
            parts.push(s);
          }
          return parts.join('');
        },
      }),
      []
    );

    const { letterMaxPx, gapPx } = useMemo(() => letterMetricsForCount(n), [n]);
    const contentWidth = n * letterMaxPx + (n - 1) * gapPx;
    const rowHeight = letterMaxPx + 8;
    const innerHeightUnscaled = rowHeight * 2 + ROW_GAP_PX;

    useLayoutEffect(() => {
      const el = containerRef.current;
      if (!el) return;
      const read = () => {
        const cs = getComputedStyle(el);
        const pl = parseFloat(cs.paddingLeft) || 0;
        const pr = parseFloat(cs.paddingRight) || 0;
        const w = el.clientWidth - pl - pr;
        if (w > 0) setContainerWidth(w);
      };
      read();
      if (typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', read);
        return () => window.removeEventListener('resize', read);
      }
      const ro = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width;
        if (w != null && w > 0) setContainerWidth(w);
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    const scale = contentWidth > 0 && containerWidth > 0 ? Math.min(1, containerWidth / contentWidth) : 1;
    const scaledHeight = innerHeightUnscaled * scale;
    const isInactive = disabled || isIntro;

    const handleTapPool = (index: number) => {
      if (isInactive) return;
      const curSlots = slotsRef.current;
      const curPool = poolRef.current;
      if (selectedSlot != null) {
        const poolLetter = curPool[index];
        const slotLetter = curSlots[selectedSlot];
        if (slotLetter == null || poolLetter == null) return;
        const nextSlots = [...curSlots];
        nextSlots[selectedSlot] = poolLetter;
        const nextPool = curPool.filter((_, i) => i !== index);
        nextPool.push(slotLetter);
        commitBoard(nextSlots, nextPool);
        setSelectedSlot(null);
        return;
      }
      const emptyIndex = curSlots.findIndex((s) => s === null);
      const letter = curPool[index];
      if (emptyIndex === -1 || letter == null) return;
      const nextSlots = [...curSlots];
      nextSlots[emptyIndex] = letter;
      commitBoard(
        nextSlots,
        curPool.filter((_, i) => i !== index)
      );
    };

    const handleTapSlot = (slotIndex: number) => {
      if (isInactive) return;
      const curSlots = slotsRef.current;
      const curPool = poolRef.current;
      const letter = curSlots[slotIndex];
      if (letter == null) {
        setSelectedSlot(null);
        return;
      }
      if (selectedSlot === slotIndex) {
        setSelectedSlot(null);
        return;
      }
      if (selectedSlot != null) {
        const nextSlots = [...curSlots];
        nextSlots[slotIndex] = curSlots[selectedSlot];
        nextSlots[selectedSlot] = letter;
        commitBoard(nextSlots, curPool);
        setSelectedSlot(null);
        return;
      }
      setSelectedSlot(slotIndex);
    };

    const returnSelectedToPool = () => {
      if (isInactive || selectedSlot == null) return;
      const curSlots = slotsRef.current;
      const curPool = poolRef.current;
      const letter = curSlots[selectedSlot];
      if (letter == null) return;
      const nextSlots = [...curSlots];
      nextSlots[selectedSlot] = null;
      commitBoard(nextSlots, [...curPool, letter]);
      setSelectedSlot(null);
    };

    const onDragStart = (source: 'pool' | 'slot', index: number) => (e: React.DragEvent) => {
      if (isInactive) {
        e.preventDefault();
        return;
      }
      setDragging({ source, index });
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', `${source}:${index}`);
    };

    const onDropSlot = (slotIndex: number) => (e: React.DragEvent) => {
      e.preventDefault();
      if (isInactive || !dragging) return;
      const curSlots = slotsRef.current;
      const curPool = poolRef.current;
      if (dragging.source === 'pool') {
        const letter = curPool[dragging.index];
        if (!letter) return;
        const nextSlots = [...curSlots];
        const displaced = nextSlots[slotIndex];
        nextSlots[slotIndex] = letter;
        let nextPool = curPool.filter((_, i) => i !== dragging.index);
        if (displaced) nextPool = [...nextPool, displaced];
        commitBoard(nextSlots, nextPool);
      } else {
        const nextSlots = [...curSlots];
        const tmp = nextSlots[slotIndex];
        nextSlots[slotIndex] = nextSlots[dragging.index];
        nextSlots[dragging.index] = tmp;
        commitBoard(nextSlots, curPool);
      }
      setDragging(null);
      setSelectedSlot(null);
    };

    if (isIntro) {
      return (
        <div className="longest-word-player longest-word-player--intro">
          <LongestWordLetterRow letters={normalized} rolling size="player" />
          <p className="bonus-player-hint">{t('bonus.player.longestWordIntroHint')}</p>
        </div>
      );
    }

    return (
      <div className="longest-word-player" ref={containerRef}>
        <p className="bonus-player-hint longest-word-player__hint">{t('bonus.player.longestWordHint')}</p>
        <div
          className="longest-word-player__board"
          style={{ height: scaledHeight, touchAction: 'none' }}
        >
          <div
            className="longest-word-player__scale"
            style={{
              width: contentWidth,
              transform: `scale(${scale})`,
              transformOrigin: 'top center',
            }}
          >
            <div className="longest-word-player__row" style={{ gap: gapPx, marginBottom: ROW_GAP_PX }}>
              {slots.map((letter, i) => (
                <button
                  key={`slot-${i}`}
                  type="button"
                  className={`longest-word-player__cell${letter ? ' longest-word-player__cell--filled' : ''}${
                    selectedSlot === i ? ' longest-word-player__cell--selected' : ''
                  }`}
                  style={{ width: letterMaxPx, height: letterMaxPx, fontSize: letterMaxPx * 0.55 }}
                  disabled={isInactive}
                  draggable={!!letter && !isInactive}
                  onDragStart={onDragStart('slot', i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={onDropSlot(i)}
                  onClick={() => handleTapSlot(i)}
                >
                  {letter ?? ''}
                </button>
              ))}
            </div>
            <div
              className="longest-word-player__row longest-word-player__pool"
              style={{ gap: gapPx }}
              onClick={returnSelectedToPool}
            >
              {pool.map((letter, i) => (
                <button
                  key={`pool-${i}-${letter}`}
                  type="button"
                  className="longest-word-player__cell longest-word-player__cell--pool"
                  style={{ width: letterMaxPx, height: letterMaxPx, fontSize: letterMaxPx * 0.55 }}
                  disabled={isInactive}
                  draggable={!isInactive}
                  onDragStart={onDragStart('pool', i)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTapPool(i);
                  }}
                >
                  {letter}
                </button>
              ))}
            </div>
          </div>
        </div>
        {currentWord ? (
          <p className="longest-word-player__preview" aria-live="polite">
            {currentWord}
          </p>
        ) : null}
      </div>
    );
  }
);
